import {
  AUDIT_RESULT,
  AuditReadinessRecord,
  SmartControlTestRecord,
} from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import {
  CONTROL_STATUS,
  Control,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { getCheckResultWithStale } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.helpers'
import { CONTROL_TEST_NAMES } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import { ControlDisplay } from './AuditScanModal.constants'

// readinessControlKey is how a control indexes anything keyed by AuditReadinessRecord.
//
// Readiness selects `control_models.id AS control_id` (auditbot/service.go,
// scanAuditReadinessRows), which is Control.id.
export const readinessControlKey = (control: Control): string => control.getId()

// Builds a controlId → SmartControlTestRecord map from each control's Operating
// Effectiveness result (the only control test we run now).
export const createControlTestMap = (
  auditReadinessLookup?: Record<string, AuditReadinessRecord>,
): Record<string, SmartControlTestRecord> => {
  const controlTestMap: Record<string, SmartControlTestRecord> = {}
  if (!auditReadinessLookup) return controlTestMap

  Object.keys(auditReadinessLookup).forEach((controlId) => {
    const oeTests = auditReadinessLookup[controlId]
      .getControlTestsList()
      .filter(
        (controlTest) =>
          controlTest.getTestName() === CONTROL_TEST_NAMES.SPOT_CHECK,
      )
    const testResult = getCheckResultWithStale(oeTests, 1)

    controlTestMap[controlId] = new SmartControlTestRecord()
      .setControlId(controlId)
      .setIsStale(testResult.isStale)
      .setResult(testResult.result)
      .setIsMissingChecks(testResult.isMissingChecks)
  })

  return controlTestMap
}

export const getFilteredControlIds = (
  controlList: Control[],
  controlTestMap: Record<string, SmartControlTestRecord>,
  filterList: ControlDisplay[],
): string[] => {
  const filteredControls = getFilteredControls(
    controlList,
    controlTestMap,
    filterList,
  )
  return filteredControls.map((control) => control.getId())
}

export const getFilteredControls = (
  controlList: Control[],
  controlTestMap: Record<string, SmartControlTestRecord>,
  filterList: ControlDisplay[],
): Control[] => {
  // First, filter out controls that are not applicable
  const applicableControls = controlList.filter(
    (control) => control.getStatus() !== CONTROL_STATUS.NOTAPPLICABLE,
  )
  if (!filterList.length || filterList.includes(ControlDisplay.ALL))
    return applicableControls
  if (filterList.includes(ControlDisplay.NONE)) return []

  const filteredControls = applicableControls.filter((control) => {
    const controlTest = controlTestMap[readinessControlKey(control)]
    const result = controlTest?.getResult()
    const isStale = controlTest?.getIsStale()
    const isPassing = result === AUDIT_RESULT.PASS
    const isFailing = result === AUDIT_RESULT.FAIL
    const isUnknown = result === AUDIT_RESULT.UNKNOWN
    const isNotRun = result === AUDIT_RESULT.NOT_RUN
    const isMissingChecks = controlTest?.getIsMissingChecks()

    if (filterList.includes(ControlDisplay.FAILED) && isFailing) return true
    if (filterList.includes(ControlDisplay.STALE) && isStale) return true
    if (filterList.includes(ControlDisplay.PASSED) && isPassing) return true
    if (filterList.includes(ControlDisplay.UNKNOWN) && isUnknown) return true
    if (filterList.includes(ControlDisplay.NOT_RUN) && isNotRun) return true
    if (filterList.includes(ControlDisplay.MISSING_CHECKS) && isMissingChecks)
      return true

    return false
  })
  return filteredControls
}
